import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Loader2, Users } from "lucide-react";

/** Print-side: én A4-side pr. QR-kode med teamnavn, dato og scanbar QR.
 *  Hænges op i baren / pavillonen / sweep-stationen så frivillige kan scanne ved ankomst.
 */
export default function PrintQuickCheckin() {
  const [params] = useSearchParams();
  const eventId = params.get("event_id");
  const roleIdsParam = params.get("role_ids"); // valgfri forhåndsvalg (komma-separeret)
  const navigate = useNavigate();
  const [qrs, setQrs] = useState([]);
  const [qrMap, setQrMap] = useState({});
  const [loading, setLoading] = useState(true);
  // null = endnu ikke initialiseret; ellers Set af role_id der skal printes
  const [selectedRoleIds, setSelectedRoleIds] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get(`${API}/quick-checkin/list?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setQrs(res.data || []))
      .catch(() => setQrs([]))
      .finally(() => setLoading(false));
  }, [eventId]);

  // Udled unikke teams (role_id) fra QR'erne — sorteret efter navn
  const teams = (() => {
    const map = new Map();
    qrs.forEach(q => {
      if (!q.role_id) return;
      if (!map.has(q.role_id)) map.set(q.role_id, { id: q.role_id, name: q.role_name || q.role_id, count: 0 });
      map.get(q.role_id).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'da', { numeric: true }));
  })();

  // Initialisér valgte teams når QR'erne er hentet (alle valgt, eller forhåndsvalg fra URL)
  useEffect(() => {
    if (selectedRoleIds !== null || teams.length === 0) return;
    if (roleIdsParam) {
      const wanted = new Set(roleIdsParam.split(',').map(s => s.trim()).filter(Boolean));
      const valid = teams.filter(t => wanted.has(t.id)).map(t => t.id);
      setSelectedRoleIds(new Set(valid.length ? valid : teams.map(t => t.id)));
    } else {
      setSelectedRoleIds(new Set(teams.map(t => t.id)));
    }
  }, [teams, selectedRoleIds, roleIdsParam]);

  const selected = selectedRoleIds || new Set();
  const toggleTeam = (id) => {
    setSelectedRoleIds(prev => {
      const next = new Set(prev || []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedRoleIds(new Set(teams.map(t => t.id)));
  const selectNone = () => setSelectedRoleIds(new Set());

  useEffect(() => {
    // Generér QR-billede pr. token
    let alive = true;
    Promise.all(qrs.map(async q => {
      const url = `${window.location.origin}/quick-checkin/${q.token}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 600, margin: 1, errorCorrectionLevel: 'M' });
      return [q.token, dataUrl];
    })).then(pairs => {
      if (alive) setQrMap(Object.fromEntries(pairs));
    });
    return () => { alive = false; };
  }, [qrs]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Sortér + filtrér QR'er: kun valgte teams. Først dato, så team-navn
  const sorted = [...qrs]
    .filter(q => selected.has(q.role_id))
    .sort((a, b) => {
      if (a.valid_date !== b.valid_date) return a.valid_date.localeCompare(b.valid_date);
      return (a.role_name || '').localeCompare(b.role_name || '', 'da', { numeric: true });
    });

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white text-zinc-900">
      {/* Toolbar (gemmes på print) */}
      <div className="no-print sticky top-0 z-30 bg-white border-b border-zinc-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-zinc-900">
            <ArrowLeft className="h-4 w-4 mr-1" /> Tilbage
          </Button>
          <h1 className="text-lg font-semibold">Hurtig indtjekning · QR-plakater ({sorted.length})</h1>
          <Button onClick={() => window.print()} disabled={sorted.length === 0} className="bg-black text-white hover:bg-zinc-800">
            <Printer className="h-4 w-4 mr-1" /> Print valgte ({sorted.length})
          </Button>
        </div>

        {/* Team-vælger: afkryds hvilke teams der skal printes */}
        {teams.length > 0 && (
          <div className="max-w-6xl mx-auto px-4 pb-3">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                <Users className="h-3 w-3" /> Vælg teams der skal printes ({selected.size} / {teams.length})
              </span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={selectAll} className="h-7 text-xs text-zinc-900" data-testid="qc-print-select-all">Alle</Button>
                <Button size="sm" variant="outline" onClick={selectNone} className="h-7 text-xs text-zinc-900" data-testid="qc-print-select-none">Ingen</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 max-h-40 overflow-y-auto border border-zinc-200 rounded p-2 bg-zinc-50">
              {teams.map(t => (
                <label key={t.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-zinc-100 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => toggleTeam(t.id)}
                    data-testid={`qc-print-team-${t.id}`}
                  />
                  <span className="truncate flex-1">{t.name}</span>
                  <span className="text-[10px] text-zinc-400 font-mono">{t.count}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Tip: Har du kun ændret ét hold, så afkryds kun det — så printer du ikke alle plakater igen. Vælg "Gem som PDF" for én samlet fil.
            </p>
          </div>
        )}
      </div>

      {/* Plakater — én pr. side */}
      <div className="max-w-3xl mx-auto py-8 print:py-0 space-y-8 print:space-y-0">
        {sorted.length === 0 ? (
          <p className="text-center text-zinc-500 italic py-16">Ingen teams valgt — afkryds mindst ét team ovenfor.</p>
        ) : sorted.map(q => {
          const dateLabel = new Date(q.valid_date).toLocaleDateString('da-DK', {
            weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
          });
          const fromTime = q.valid_from?.slice(11, 16);
          const untilTime = q.valid_until?.slice(11, 16);
          return (
            <div
              key={q.token}
              className="qc-poster bg-white mx-auto"
              data-testid={`qc-poster-${q.token}`}
            >
              {/* Header */}
              <div className="qc-header">
                <div className="qc-eyebrow">HURTIG INDTJEKNING</div>
                <h1 className="qc-role">{q.role_name}</h1>
                <div className="qc-date">{dateLabel}</div>
                <div className="qc-time">Gyldig: {fromTime} → {untilTime}</div>
              </div>

              {/* QR */}
              <div className="qc-qr-wrap">
                {qrMap[q.token] ? (
                  <img src={qrMap[q.token]} alt={`QR for ${q.role_name}`} className="qc-qr-img" />
                ) : (
                  <div className="qc-qr-placeholder">
                    <Loader2 className="h-12 w-12 animate-spin text-zinc-300" />
                  </div>
                )}
              </div>

              {/* Bottom instructions */}
              <div className="qc-instructions">
                <div className="qc-step"><span className="qc-step-num">1</span>Scan QR med din mobil</div>
                <div className="qc-step"><span className="qc-step-num">2</span>Indtast dit fulde navn</div>
                <div className="qc-step"><span className="qc-step-num">3</span>Du er nu tjekket ind i {q.session_duration_hours} timer</div>
              </div>

              {q.capacity ? (
                <div className="qc-capacity">Max {q.capacity} personer ad gangen</div>
              ) : null}
            </div>
          );
        })}
      </div>

      <style>{`
        .qc-poster {
          width: 210mm;
          height: 296mm;
          padding: 18mm 25mm 16mm;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-family: 'Arial Black', Arial, sans-serif;
          box-sizing: border-box;
          color: #111;
          border: 1px solid #e4e4e7;
          overflow: hidden;
        }
        .qc-header { text-align: center; margin-bottom: 5mm; width: 100%; flex-shrink: 0; }
        .qc-eyebrow {
          font-size: 11pt; letter-spacing: 4px; color: #059669;
          font-weight: 900; margin-bottom: 3mm;
        }
        .qc-role { font-size: 38pt; line-height: 1.05; margin: 0 0 4mm; font-weight: 900; }
        .qc-date { font-size: 16pt; color: #444; margin-bottom: 1.5mm; }
        .qc-time { font-size: 13pt; color: #666; font-family: 'Courier New', monospace; }
        .qc-qr-wrap {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 4mm 0;
          width: 100%;
        }
        .qc-qr-img { width: auto; height: 100%; max-width: 100%; max-height: 130mm; object-fit: contain; image-rendering: pixelated; }
        .qc-qr-placeholder {
          width: 120mm; height: 100%; max-height: 120mm; background: #f4f4f5;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8mm;
        }
        .qc-instructions { width: 100%; margin-top: 4mm; flex-shrink: 0; }
        .qc-step {
          display: flex; align-items: center; gap: 4mm;
          font-size: 14pt; margin: 2mm 0;
        }
        .qc-step-num {
          width: 10mm; height: 10mm;
          background: #059669; color: #fff;
          border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 11pt; font-weight: 900;
          flex-shrink: 0;
        }
        .qc-capacity {
          margin-top: 4mm;
          font-size: 11pt;
          color: #666;
          font-style: italic;
          text-align: center;
          width: 100%;
          flex-shrink: 0;
        }
        @media print {
          @page { size: A4; margin: 0; }
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .qc-poster {
            border: none !important;
            page-break-after: always;
            margin: 0 !important;
          }
          .qc-poster:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}
